import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError, API } from "@/lib/api";
import { formatVariantLabel, sortVariants } from "@/lib/variants";
import { fetchAvailability, aggregateForProduct } from "@/lib/stockAvailability";
import StockAvailabilityPanel from "@/components/StockAvailabilityPanel";
import StockOptionDialog from "@/components/StockOptionDialog";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Package, Layers, Warehouse as WarehouseIcon, Image as ImageIcon,
  ChevronDown, ChevronUp, Plus, Trash2, Upload, Download, Save, Star,
  TrendingUp, TrendingDown, ArrowUpCircle, ArrowDownCircle, FileSpreadsheet,
  Link2, Loader2, X, Edit3, AlertTriangle, Boxes, FileText, Copy,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { duplicateProduct } from "@/lib/duplicateProduct";
import RecipeSheet from "@/components/RecipeSheet";
import BarcodeLabelDialog from "@/components/BarcodeLabelDialog";
import LabelsTab from "@/components/LabelsTab";

const fmt = (n) => Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => (d ? new Date(d).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" }) : "—");

const KIND_META = {
  manuel: { label: "Manuel", color: "text-slate-500", icon: Edit3 },
  sale: { label: "Satış", color: "text-emerald-500", icon: TrendingDown },
  purchase: { label: "Alış", color: "text-blue-500", icon: TrendingUp },
  production: { label: "Üretim", color: "text-amber-500", icon: Package },
  shipment: { label: "Sevkiyat", color: "text-purple-500", icon: TrendingDown },
  fire: { label: "Fire", color: "text-rose-500", icon: AlertTriangle },
};

const SUPPLY_TYPE_LABEL = {
  manufactured: "ÜRETİM ÜRÜNÜ",
  traded: "TİCARİ ÜRÜN",
  outsourced: "FASON / DIŞ ÜRETİM",
  unknown: "BELİRLENMEMİŞ",
};

// ---- Ürün Tipi rozeti + Değiştir (mevcut supply_type alanı, PATCH reuse) ---
function SupplyTypeControl({ product, canEdit, onChanged }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const current = product.supply_type || "manufactured";
  const change = async (v) => {
    if (v === current || saving) { setOpen(false); return; }
    setSaving(true);
    try {
      await api.patch(`/products/${product.id}`, { supply_type: v });
      toast.success(`Ürün tipi güncellendi: ${SUPPLY_TYPE_LABEL[v]}`);
      setOpen(false);
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  const optCls = (v) =>
    `w-full text-left px-3 py-2 rounded-sm border text-xs font-semibold transition-colors ${
      v === current
        ? "border-primary bg-primary/10 text-primary"
        : "border-border bg-muted/20 hover:bg-muted/50"
    }`;
  return (
    <div className="flex items-center gap-2 mt-1">
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Ürün Tipi:</span>
      <span
        className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm border border-primary/40 bg-primary/10 text-primary"
        data-testid="supply-type-badge"
      >
        {SUPPLY_TYPE_LABEL[current] || current}
      </span>
      {canEdit && (
        <Button
          variant="outline" size="sm"
          className="rounded-sm h-6 px-2 text-[10px] uppercase tracking-wider"
          data-testid="supply-type-change-btn"
          onClick={() => setOpen(true)}
        >
          Değiştir
        </Button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" data-testid="supply-type-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm">Ürün Tipi Değiştir</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <button type="button" className={optCls("traded")} data-testid="supply-type-option-traded" onClick={() => change("traded")} disabled={saving}>
              TİCARİ ÜRÜN
              <span className="block text-[10px] font-normal text-muted-foreground mt-0.5">Hazır alım-satım ürünü — stok rezerve edilir, eksik satın almaya gider.</span>
            </button>
            <button type="button" className={optCls("manufactured")} data-testid="supply-type-option-manufactured" onClick={() => change("manufactured")} disabled={saving}>
              ÜRETİM ÜRÜNÜ
              <span className="block text-[10px] font-normal text-muted-foreground mt-0.5">Ürün reçetesi üzerinden üretim akışı tetiklenir.</span>
            </button>
          </div>
          {saving && <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Kaydediliyor…</div>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [variants, setVariants] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [stockSummary, setStockSummary] = useState(null);
  // FAZ 2C — canonical availability (null = yüklenemedi, [] = kayıt yok)
  const [availRows, setAvailRows] = useState(null);
  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState({});
  const [tab, setTab] = useState("genel");
  const canEdit = can("products", "edit");
  const tabsListRef = useRef(null);
  // Mobil: ilk açılışta tab scroll'u en solda (Genel görünür), tab değişince aktif tab görünür alana kayar
  useEffect(() => { if (tabsListRef.current) tabsListRef.current.scrollLeft = 0; }, [id]);
  useEffect(() => {
    const el = tabsListRef.current?.querySelector(`[data-testid="tab-${tab}"]`);
    el?.scrollIntoView({ behavior: "smooth", inline: "nearest", block: "nearest" });
  }, [tab]);
  const generalSaveRef = useRef(null);
  const onCopy = async () => {
    const created = await duplicateProduct(product);
    if (created) navigate(`/urun/${created.id}`);
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [p, v, w, ss, imgs] = await Promise.all([
        api.get(`/products/${id}`),
        api.get(`/products/${id}/variants`),
        api.get(`/warehouses`),
        api.get(`/products/${id}/stock-summary`),
        api.get(`/products/${id}/images`),
      ]);
      setProduct(p.data);
      setVariants(v.data || []);
      setWarehouses((w.data || []).filter((x) => x.is_active !== false));
      setStockSummary(ss.data);
      setImages(imgs.data || []);
      // FAZ 2C — canonical availability (hata durumunda null; 0 gibi gösterilmez)
      fetchAvailability({ productIds: [id] })
        .then(setAvailRows)
        .catch(() => setAvailRows(null));
      // Preload image blobs
      for (const im of imgs.data || []) {
        api.get(`/products/${id}/images/${im.id}/download`, { responseType: "blob" })
          .then((r) => setImagePreviews((prev) => ({ ...prev, [im.id]: URL.createObjectURL(r.data) })))
          .catch(() => {});
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { loadAll(); }, [loadAll]);

  if (loading || !product) {
    return (
      <div>
        <PageHeader title="Ürün Detayı" subtitle="Yükleniyor..." breadcrumbs={["Ana Sayfa", "Ürünler"]} />
        <div className="p-8 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#F1F3F6] dark:bg-transparent min-h-full text-slate-700 dark:text-inherit">
      <PageHeader
        title={product.name}
        subtitle={product.category || "Ürün Detayı"}
        breadcrumbs={["Ana Sayfa", "Ürünler", product.name]}
      />

      <div className="p-4 pb-28 lg:p-8 lg:pb-28 max-w-7xl mx-auto space-y-4 lg:space-y-5">
        {/* Top bar — mobil-güvenli 3 satır: isim / metadata chipleri / aksiyonlar */}
        <div className="space-y-2 lg:space-y-0 lg:flex lg:items-center lg:gap-3 lg:flex-wrap">
          <div className="flex items-center gap-2 lg:min-w-0">
            <Button variant="outline" size="sm" onClick={() => navigate("/urunler")} className="rounded-sm px-2 shrink-0" data-testid="pd-back-btn" title="Ürünlere dön">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="font-bold text-lg leading-tight flex-1 min-w-0 break-words lg:flex-initial lg:max-w-md lg:truncate">{product.name}</div>
            <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm border border-emerald-500/40 bg-emerald-500/10 text-emerald-500" data-testid="pd-active-badge">Aktif</span>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap lg:flex-1" data-testid="pd-meta-chips">
            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border border-border bg-muted/30 text-muted-foreground font-mono" data-testid="pd-id-chip">{(product.id || "").slice(0, 8).toUpperCase()}</span>
            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border border-border bg-muted/30 text-muted-foreground font-mono" data-testid="pd-code-chip">{product.product_code || "—"}</span>
            <SupplyTypeControl product={product} canEdit={canEdit} onChanged={loadAll} />
            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border border-border bg-muted/30 text-muted-foreground" data-testid="pd-category-chip">{product.category || "Kategori yok"}</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap lg:ml-auto" data-testid="pd-actions">
            {canEdit && (
              <Button variant="outline" size="sm" className="rounded-sm" onClick={onCopy} data-testid="pd-copy-btn">
                <Copy className="w-4 h-4 mr-1" /> Kopyala
              </Button>
            )}
            <Button variant="outline" size="sm" className="rounded-sm" onClick={() => setTab("varyant")} data-testid="pd-variants-btn">
              <Layers className="w-4 h-4 mr-1" /> Varyantlar
            </Button>
            {canEdit && (
              <Button size="sm" className="rounded-sm bg-primary hover:bg-primary/90" onClick={() => { setTab("genel"); setTimeout(() => generalSaveRef.current?.(), 0); }} data-testid="pd-save-btn">
                <Save className="w-4 h-4 mr-1" /> Kaydet
              </Button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList ref={tabsListRef} className="rounded-sm bg-muted/40 border border-border max-w-full overflow-x-auto whitespace-nowrap justify-start" data-testid="product-tabs">
            <TabsTrigger value="genel" data-testid="tab-genel" className="rounded-sm shrink-0">Genel</TabsTrigger>
            <TabsTrigger value="varyant" data-testid="tab-varyant" className="rounded-sm shrink-0">Varyantlar ({variants.length})</TabsTrigger>
            <TabsTrigger value="stok" data-testid="tab-stok" className="rounded-sm shrink-0">Stok</TabsTrigger>
            <TabsTrigger value="recete" data-testid="tab-recete" className="rounded-sm shrink-0">
              <FileText className="w-3.5 h-3.5 mr-1" /> Reçete
            </TabsTrigger>
            <TabsTrigger value="gecmis" data-testid="tab-gecmis" className="rounded-sm shrink-0">Geçmiş</TabsTrigger>
            <TabsTrigger value="resimler" data-testid="tab-resimler" className="rounded-sm shrink-0">Resimler ({images.length})</TabsTrigger>
            <TabsTrigger value="etiketler" data-testid="tab-etiketler" className="rounded-sm shrink-0">Etiketler</TabsTrigger>
            <TabsTrigger value="bagli" data-testid="tab-bagli" className="rounded-sm shrink-0">Bağlı Ürünler</TabsTrigger>
            <TabsTrigger value="diger" data-testid="tab-diger" className="rounded-sm shrink-0">Diğer</TabsTrigger>
          </TabsList>

          <TabsContent value="genel" className="mt-4">
            <GeneralTab
              product={product}
              canEdit={canEdit}
              variants={variants}
              warehouses={warehouses}
              summary={stockSummary}
              availRows={availRows}
              images={images}
              previews={imagePreviews}
              onSave={loadAll}
              registerSave={generalSaveRef}
              onGoTab={setTab}
            />
          </TabsContent>
          <TabsContent value="varyant" className="mt-4">
            <VariantsTab productId={id} variants={variants} onReload={loadAll} />
          </TabsContent>
          <TabsContent value="stok" className="mt-4">
            <StockTab
              product={product}
              productId={id}
              variants={variants}
              warehouses={warehouses}
              summary={stockSummary}
              availRows={availRows}
              onReload={loadAll}
            />
          </TabsContent>
          <TabsContent value="recete" className="mt-4">
            <RecipeTabContent product={product} />
          </TabsContent>
          <TabsContent value="gecmis" className="mt-4">
            <HistoryTab productId={id} variants={variants} />
          </TabsContent>
          <TabsContent value="resimler" className="mt-4">
            <ImagesTab
              productId={id}
              images={images}
              previews={imagePreviews}
              onReload={loadAll}
            />
          </TabsContent>
          <TabsContent value="bagli" className="mt-4">
            <LinkedTab product={product} onSave={loadAll} />
          </TabsContent>
          <TabsContent value="etiketler" className="mt-4">
            <LabelsTab productId={id} />
          </TabsContent>
          <TabsContent value="diger" className="mt-4">
            <OtherTab product={product} onSave={loadAll} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// ---- Recipe tab (embedded in ProductDetail) -------------------------------
function RecipeTabContent({ product }) {
  const [open, setOpen] = useState(true);
  return (
    <div>
      <div className="border border-border rounded-sm bg-muted/20 p-6 text-center space-y-4">
        <FileText className="w-10 h-10 mx-auto text-blue-500" />
        <div className="text-slate-800 dark:text-slate-100 font-semibold">Ürün Reçetesi & Maliyet</div>
        <div className="text-xs text-muted-foreground max-w-md mx-auto">
          Kumaş, aksesuar, operasyon adımları ve maliyet analizi. Genel gider ve kâr marjı yüzdesi değiştiğinde birim maliyet anlık güncellenir.
        </div>
        <Button onClick={() => setOpen(true)} className="bg-blue-600 hover:bg-blue-500 rounded-sm">
          <FileText className="w-4 h-4 mr-2" /> Reçeteyi Aç / Düzenle
        </Button>
      </div>
      <RecipeSheet product={product} open={open} onOpenChange={setOpen} />
    </div>
  );
}

// ---- General tab ----------------------------------------------------------
function GeneralTab({ product, canEdit, variants = [], warehouses = [], summary = null, availRows = null, images = [], previews = {}, onSave, registerSave, onGoTab }) {
  const [name, setName] = useState(product.name || "");
  const [description, setDescription] = useState(product.description || "");
  const [category, setCategory] = useState(product.category || "");
  const [code, setCode] = useState(product.product_code || "");
  const [brand, setBrand] = useState(product.brand || "");
  const [critical, setCritical] = useState(product.critical_stock ?? 0);
  const [categoryDefs, setCategoryDefs] = useState([]);
  const [brandDefs, setBrandDefs] = useState([]);
  const [purchasePrice, setPurchasePrice] = useState(product.purchase_price || product.base_cost || 0);
  const [purchaseTax, setPurchaseTax] = useState(product.purchase_tax_rate ?? 20);
  const [purchaseCur, setPurchaseCur] = useState((product.purchase_currency || "TRY").toUpperCase());
  const [purchaseDiscount, setPurchaseDiscount] = useState(product.purchase_discount_percent ?? 0);
  const [salePrice, setSalePrice] = useState(product.sale_price || product.base_price || 0);
  const [saleTax, setSaleTax] = useState(product.sale_tax_rate ?? 20);
  const [saleCur, setSaleCur] = useState((product.sale_currency || "TRY").toUpperCase());
  // Perakende Fiyatlandırma (FAZ 1) — canonical: retail_markup_percent + pricing_settings.default_markup_percent
  const [markupRate, setMarkupRate] = useState(product.retail_markup_percent ?? "");
  const [defaultMarkup, setDefaultMarkup] = useState(50);
  const [saleManual, setSaleManual] = useState(Number(product.sale_price || product.base_price) > 0);
  const [saving, setSaving] = useState(false);
  const [newCatOpen, setNewCatOpen] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [newCatSaving, setNewCatSaving] = useState(false);

  const PRICE_CURRENCIES = ["TRY", "USD", "EUR", "GBP"];
  const CUR_SYM = { TRY: "₺", USD: "$", EUR: "€", GBP: "£" };

  const loadCategories = useCallback(() => {
    api.get("/definitions/product_category")
      .then((r) => setCategoryDefs((r.data || []).filter((d) => d.is_active !== false)))
      .catch(() => setCategoryDefs([]));
  }, []);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  useEffect(() => {
    api.get("/definitions/brand")
      .then((r) => setBrandDefs((r.data || []).filter((d) => d.is_active !== false)))
      .catch(() => {});
  }, []);

  // FAZ 1 — tenant varsayılan artış oranı
  useEffect(() => {
    api.get("/products/pricing-settings")
      .then((r) => setDefaultMarkup(Number(r.data?.default_markup_percent ?? 50)))
      .catch(() => {});
  }, []);

  // FAZ 1 — önerilen fiyat: NET ALIŞ × (100 + markup) / 100; YUVARLAMA YOK.
  // Net alış = alış × (1 - alış iskonto / 100). İskonto 0/boş ise net = alış (geriye dönük).
  const effRate = (markupRate === "" || markupRate === null || markupRate === undefined) ? defaultMarkup : Number(markupRate);
  const purchaseNum = Number(purchasePrice);
  const purchaseDiscountNum = Number(purchaseDiscount || 0);
  const netPurchaseNum = Number.isFinite(purchaseNum) && purchaseNum > 0
    ? purchaseNum * (100 - (Number.isFinite(purchaseDiscountNum) && purchaseDiscountNum > 0 ? purchaseDiscountNum : 0)) / 100
    : null;
  const priceCurMatch = purchaseCur === saleCur;
  const suggestedPrice = priceCurMatch && netPurchaseNum !== null && Number.isFinite(effRate)
    ? (netPurchaseNum * (100 + effRate)) / 100
    : null;
  const fmtPrice = (n) => {
    const v = Number(n);
    if (!Number.isFinite(v)) return "—";
    const r2 = Math.round(v * 100) / 100;
    const more = Math.abs(v - r2) > 1e-9;
    return v.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: more ? 4 : 2 });
  };

  // Manuel satış fiyatı korunur — sadece fiyat hiç girilmemişse (0/boş) öneri başlangıç değeri olur.
  useEffect(() => {
    if (saleManual || suggestedPrice === null) return;
    if (Number(salePrice) !== suggestedPrice) setSalePrice(suggestedPrice);
  }, [suggestedPrice, saleManual]);

  const createCategory = async () => {
    const nm = newCatName.trim();
    if (!nm) return;
    setNewCatSaving(true);
    try {
      const { data } = await api.post("/definitions/product_category", { name: nm });
      toast.success(`"${data.name}" kategorisi eklendi`);
      setNewCatName("");
      setNewCatOpen(false);
      await loadCategories();
      setCategory(data.name);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setNewCatSaving(false); }
  };

  const marginPct = salePrice > 0 && purchasePrice > 0
    ? ((salePrice - purchasePrice) / salePrice) * 100
    : 0;

  // Kategori seçili değere göre bilinen liste yoksa özel değer olarak kalır.
  const categoryInList = useMemo(
    () => !category || categoryDefs.some((d) => d.name === category),
    [category, categoryDefs]
  );
  const brandInList = useMemo(
    () => !brand || brandDefs.some((d) => d.name === brand),
    [brand, brandDefs]
  );

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/products/${product.id}`, {
        name,
        description,
        category: category || null,
        product_code: code || "",
        brand: brand || "",
        critical_stock: Number(critical) || 0,
        purchase_price: Number(purchasePrice) || 0,
        purchase_tax_rate: Number(purchaseTax) || 0,
        purchase_currency: purchaseCur,
        purchase_discount_percent: Number(purchaseDiscount) || 0,
        sale_price: Number(salePrice) || 0,
        sale_tax_rate: Number(saleTax) || 0,
        sale_currency: saleCur,
        retail_markup_percent: (markupRate === "" || markupRate === null || markupRate === undefined) ? null : Number(markupRate),
        base_cost: Number(purchasePrice) || 0,
        base_price: Number(salePrice) || 0,
      });
      toast.success("Kaydedildi");
      onSave?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  // Header'daki Kaydet butonu bu fonksiyonu çağırır (her render güncel ref)
  useEffect(() => { if (registerSave) registerSave.current = save; });

  const firstBarcode = (variants.find((v) => (v.barcode || "").trim()) || {}).barcode || "—";

  return (
    <div className="space-y-4 lg:space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-[300px_minmax(0,1fr)_320px] gap-4 lg:gap-5 items-start">
        {/* SOL — ürün görseli */}
        <ProductImageCard images={images} previews={previews} onGoTab={onGoTab} />

        {/* ORTA — genel bilgiler + fiyatlandırma */}
        <div className="space-y-5 min-w-0">
          <Card className="rounded-sm bg-[#FCFCFD] dark:bg-card border border-slate-300 dark:border-border shadow-[0_1px_2px_rgba(16,24,40,0.05),0_8px_24px_-12px_rgba(16,24,40,0.10)] dark:shadow-md dark:shadow-black/30 p-5 space-y-4">
            <div className="text-[13px] font-semibold uppercase tracking-wider text-slate-800 dark:text-foreground">Genel Bilgiler</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-[11px] uppercase tracking-wide font-medium text-slate-500 dark:text-muted-foreground">Ürün Adı</Label>
                <Input
                  data-testid="product-name"
                  value={name} onChange={(e) => setName(e.target.value)}
                  disabled={!canEdit}
                  className="rounded-sm mt-1 font-medium bg-slate-50 dark:bg-background border-slate-300 dark:border-input"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide font-medium text-slate-500 dark:text-muted-foreground">Ürün Kodu / SKU</Label>
                <Input
                  data-testid="general-code"
                  value={code} onChange={(e) => setCode(e.target.value)}
                  disabled={!canEdit}
                  className="rounded-sm mt-1 font-medium bg-slate-50 dark:bg-background border-slate-300 dark:border-input" placeholder="TSRT-001"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide font-medium text-slate-500 dark:text-muted-foreground">Barkod</Label>
                <div
                  data-testid="general-barcode"
                  className="mt-1 px-3 py-2 rounded-sm border border-slate-300 dark:border-border bg-slate-100 dark:bg-muted/30 font-mono text-sm text-slate-600 dark:text-muted-foreground"
                  title="Barkod varyant seviyesinde tutulur — Varyantlar sekmesinden yönetilir"
                >
                  {firstBarcode}
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between gap-2">
                  <Label className="text-[11px] uppercase tracking-wide font-medium text-slate-500 dark:text-muted-foreground flex items-center gap-1">
                    Kategori
                    {!category && (
                      <span className="text-[10px] text-amber-500 normal-case font-sans">· seçilmedi</span>
                    )}
                  </Label>
                  <button
                    type="button"
                    data-testid="general-category-new-btn"
                    onClick={() => setNewCatOpen(true)}
                    className="text-[11px] uppercase text-primary hover:text-primary/80 flex items-center gap-0.5 transition-colors"
                  >
                    <Plus className="w-3 h-3" /> Yeni Kategori
                  </button>
                </div>
                <Select
                  value={category || "__none__"}
                  onValueChange={(v) => setCategory(v === "__none__" ? "" : v)}
                >
                  <SelectTrigger data-testid="general-category" className="rounded-sm mt-1 bg-slate-50 dark:bg-background border-slate-300 dark:border-input">
                    <SelectValue placeholder="Kategori seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Kategori Yok —</SelectItem>
                    {!categoryInList && category && (
                      <SelectItem value={category}>{category} (özel)</SelectItem>
                    )}
                    {categoryDefs.map((d) => (
                      <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide font-medium text-slate-500 dark:text-muted-foreground">Marka</Label>
                <Select
                  value={brand || "__none__"}
                  onValueChange={(v) => setBrand(v === "__none__" ? "" : v)}
                >
                  <SelectTrigger data-testid="general-brand" className="rounded-sm mt-1 bg-slate-50 dark:bg-background border-slate-300 dark:border-input">
                    <SelectValue placeholder="Marka seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Marka Yok —</SelectItem>
                    {!brandInList && brand && (
                      <SelectItem value={brand}>{brand} (özel)</SelectItem>
                    )}
                    {brandDefs.map((d) => (
                      <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide font-medium text-slate-500 dark:text-muted-foreground">Ürün Tipi</Label>
                <div
                  data-testid="general-supply-type"
                  className="mt-1 px-3 py-2 rounded-sm border border-primary/40 bg-primary/10 text-[11px] font-bold text-primary uppercase tracking-wider"
                >
                  {SUPPLY_TYPE_LABEL[product.supply_type || "manufactured"]}
                </div>
              </div>
              <div className="md:col-span-3">
                <Label className="text-[11px] uppercase tracking-wide font-medium text-slate-500 dark:text-muted-foreground">Açıklama</Label>
                <Textarea
                  data-testid="product-description"
                  rows={2}
                  value={description} onChange={(e) => setDescription(e.target.value)}
                  className="rounded-sm mt-1 resize-none bg-slate-50 dark:bg-background border-slate-300 dark:border-input"
                />
              </div>
            </div>
          </Card>

          <Card className="rounded-sm bg-[#FCFCFD] dark:bg-card border border-slate-300 dark:border-border shadow-[0_1px_2px_rgba(16,24,40,0.05),0_8px_24px_-12px_rgba(16,24,40,0.10)] dark:shadow-md dark:shadow-black/30 p-4 space-y-3">
            <div className="text-[13px] font-semibold uppercase tracking-wider text-slate-800 dark:text-foreground">Fiyatlandırma</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div className="col-span-2 md:col-span-1">
                <Label className="text-[11px] uppercase tracking-wide font-medium text-blue-500 flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" /> Alış Fiyatı
                </Label>
                <Input
                  data-testid="product-purchase-price"
                  type="number" step="0.01" min="0"
                  value={purchasePrice}
                  onChange={(e) => setPurchasePrice(e.target.value)}
                  className="rounded-sm mt-1 font-medium bg-slate-50 dark:bg-background border-slate-300 dark:border-input"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide font-medium text-blue-500">Alış Para Birimi</Label>
                <Select value={purchaseCur} onValueChange={setPurchaseCur}>
                  <SelectTrigger data-testid="product-purchase-currency" className="rounded-sm mt-1 font-medium bg-slate-50 dark:bg-background border-slate-300 dark:border-input">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRICE_CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c === "TRY" ? "TL" : c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide font-medium text-blue-500">Alış KDV %</Label>
                <Input
                  data-testid="product-purchase-tax"
                  type="number" step="0.5" min="0"
                  value={purchaseTax}
                  onChange={(e) => setPurchaseTax(e.target.value)}
                  className="rounded-sm mt-1 font-medium bg-slate-50 dark:bg-background border-slate-300 dark:border-input"
                />
              </div>
              <div className="col-span-2 md:col-span-1">
                <Label className="text-[11px] uppercase tracking-wide font-medium text-blue-500">Alış İskonto Oranı %</Label>
                <Input
                  data-testid="product-purchase-discount"
                  type="number" step="0.1" min="0" max="100"
                  value={purchaseDiscount}
                  onChange={(e) => setPurchaseDiscount(e.target.value)}
                  placeholder="0"
                  className="rounded-sm mt-1 font-medium bg-slate-50 dark:bg-background border-slate-300 dark:border-input"
                />
              </div>
            </div>
            <div className="text-[11px] text-slate-600 dark:text-muted-foreground -mt-1">
              KDV dahil: {CUR_SYM[purchaseCur] || "₺"}{fmt(Number(purchasePrice) * (1 + Number(purchaseTax) / 100))}
              {Number(purchaseDiscount) > 0 && netPurchaseNum !== null && (
                <> · Net Alış: {CUR_SYM[purchaseCur] || "₺"}{fmtPrice(netPurchaseNum)}</>
              )}
            </div>

            {/* Perakende Fiyatlandırma — FAZ 1 bandı */}
            <div className="rounded-sm border border-primary/40 bg-primary/5 p-3 space-y-2" data-testid="retail-pricing-band">
              <div className="text-[10px] uppercase text-primary tracking-widest">Perakende Fiyatlandırma</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 items-end">
                <div>
                  <Label className="text-[11px] uppercase tracking-wide font-medium text-slate-500 dark:text-muted-foreground">Perakende Artış Oranı %</Label>
                  <Input
                    data-testid="product-markup-rate"
                    type="number" step="0.1" min="0"
                    value={markupRate}
                    onChange={(e) => setMarkupRate(e.target.value)}
                    placeholder={String(defaultMarkup)}
                    className="rounded-sm mt-1 font-medium bg-slate-50 dark:bg-background border-slate-300 dark:border-input"
                  />
                  <div className="text-[11px] text-slate-500 dark:text-muted-foreground mt-1">
                    Boş bırakırsanız şirket varsayılanı (%{fmtPrice(defaultMarkup)}) kullanılır.
                  </div>
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wide font-medium text-slate-500 dark:text-muted-foreground">Önerilen Perakende Satış Fiyatı</Label>
                  <div
                    data-testid="product-suggested-price"
                    className="mt-1 px-3 py-1.5 rounded-sm border border-emerald-500/40 bg-emerald-500/10 text-base font-bold text-emerald-500"
                  >
                    {suggestedPrice !== null ? `${CUR_SYM[saleCur] || "₺"}${fmtPrice(suggestedPrice)}` : "—"}
                  </div>
                  {suggestedPrice !== null && (
                    <div className="text-[11px] text-slate-500 dark:text-muted-foreground mt-1">
                      ({fmtPrice(purchaseNum)} × {(1 + effRate / 100).toLocaleString("tr-TR")})
                    </div>
                  )}
                </div>
                <div className="col-span-2 md:col-span-1">
                  {suggestedPrice !== null && Number(salePrice) !== suggestedPrice && (
                    <Button
                      type="button"
                      data-testid="apply-suggested-price"
                      onClick={() => { setSalePrice(suggestedPrice); setSaleManual(true); }}
                      className="rounded-sm bg-cyan-600 hover:bg-cyan-500 uppercase tracking-wider text-[10px] font-semibold w-full"
                    >
                      Önerilen Fiyatı Uygula
                    </Button>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div className="col-span-2 md:col-span-1">
                <Label className="text-[11px] uppercase tracking-wide font-medium text-emerald-500 flex items-center gap-1">
                  <TrendingDown className="w-3 h-3 rotate-180" /> Perakende Satış Fiyatı
                </Label>
                <Input
                  data-testid="product-sale-price"
                  type="number" step="0.01" min="0"
                  value={salePrice}
                  onChange={(e) => { setSaleManual(true); setSalePrice(e.target.value); }}
                  className="rounded-sm mt-1 font-medium bg-slate-50 dark:bg-background border-slate-300 dark:border-input"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide font-medium text-emerald-500">Satış Para Birimi</Label>
                <Select value={saleCur} onValueChange={setSaleCur}>
                  <SelectTrigger data-testid="product-sale-currency" className="rounded-sm mt-1 font-medium bg-slate-50 dark:bg-background border-slate-300 dark:border-input">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRICE_CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c === "TRY" ? "TL" : c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide font-medium text-emerald-500">Satış KDV %</Label>
                <Input
                  data-testid="product-sale-tax"
                  type="number" step="0.5" min="0"
                  value={saleTax}
                  onChange={(e) => setSaleTax(e.target.value)}
                  className="rounded-sm mt-1 font-medium bg-slate-50 dark:bg-background border-slate-300 dark:border-input"
                />
              </div>
            </div>
            <div className="text-[11px] text-slate-600 dark:text-muted-foreground -mt-1">
              KDV dahil: {CUR_SYM[saleCur] || "₺"}{fmt(Number(salePrice) * (1 + Number(saleTax) / 100))}
              {marginPct > 0 && (
                <span className="ml-2 text-primary font-semibold">· Kâr Marjı: %{marginPct.toFixed(1)}</span>
              )}
            </div>
            <div className="text-[11px] text-slate-500 dark:text-muted-foreground flex items-center gap-1.5 border-t border-border pt-2 leading-snug">
              <AlertTriangle className="w-3 h-3 text-amber-500 shrink-0" />
              Alış fiyatı veya artış oranı değiştiğinde satış fiyatı otomatik olarak güncellenmez. İsterseniz önerilen fiyatı uygulayabilirsiniz.
            </div>
            {!priceCurMatch && purchaseNum > 0 && (
              <div data-testid="product-currency-mismatch-warning" className="text-[11px] text-amber-500">
                ⚠ Alış ({purchaseCur}) ve satış ({saleCur}) para birimleri farklı — otomatik perakende önerisi hesaplanmadı.
              </div>
            )}
          </Card>
        </div>

        {/* SAĞ — stok + diğer bilgiler */}
        <div className="space-y-5 md:col-span-2 lg:col-span-1">
          <StockInfoCard productId={product.id} summary={summary} warehouses={warehouses} onGoTab={onGoTab} availRows={availRows} />
          <Card className="rounded-sm bg-[#FCFCFD] dark:bg-card border border-slate-300 dark:border-border shadow-[0_1px_2px_rgba(16,24,40,0.05),0_8px_24px_-12px_rgba(16,24,40,0.10)] dark:shadow-md dark:shadow-black/30 p-4 space-y-3" data-testid="pd-other-card">
            <div className="text-[13px] font-semibold uppercase tracking-wider text-slate-800 dark:text-foreground">Diğer Bilgiler</div>
            <div>
              <Label className="text-[11px] uppercase tracking-wide font-medium text-slate-500 dark:text-muted-foreground flex items-center gap-1">
                <AlertTriangle className="w-3 h-3 text-amber-500" /> Minimum Stok (Kritik Seviye)
              </Label>
              <Input
                data-testid="pd-critical-stock"
                type="number" min="0"
                value={critical}
                onChange={(e) => setCritical(e.target.value)}
                className="rounded-sm mt-1 font-medium bg-slate-50 dark:bg-background border-slate-300 dark:border-input"
              />
              <div className="text-[11px] text-slate-500 dark:text-muted-foreground mt-1">
                Stok bu seviyenin altına düştüğünde uyarı gösterilir.
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* ALT — fiyat geçmişi (mevcut history endpoint'leri REUSE) */}
      <PriceHistoryCard productId={product.id} onGoTab={onGoTab} />

      <div className="flex items-center justify-end">
        <Button
          data-testid="product-save-general"
          onClick={save} disabled={saving}
          className="rounded-sm bg-primary hover:bg-primary/90"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
          Kaydet
        </Button>
      </div>

      <Dialog open={newCatOpen} onOpenChange={setNewCatOpen}>
        <DialogContent className="rounded-sm sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Yeni Kategori Ekle</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wide font-medium text-slate-500 dark:text-muted-foreground">Kategori Adı</Label>
              <Input
                data-testid="general-category-new-input"
                autoFocus
                value={newCatName}
                onChange={(e) => setNewCatName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") createCategory(); }}
                placeholder="Örn: İş Ayakkabısı"
                className="rounded-sm mt-1 bg-slate-50 dark:bg-background border-slate-300 dark:border-input"
              />
            </div>
            <div className="text-[11px] text-muted-foreground">
              Eklenen kategori tüm ürünler için Tanımlar listesine kaydedilir ve otomatik seçilir.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewCatOpen(false)} className="rounded-sm">
              İptal
            </Button>
            <Button
              data-testid="general-category-new-save"
              onClick={createCategory}
              disabled={newCatSaving || !newCatName.trim()}
              className="rounded-sm bg-primary hover:bg-primary/90"
            >
              {newCatSaving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
              Ekle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---- Sol: ürün görseli kartı (mevcut images altyapısı REUSE) --------------
function ProductImageCard({ images, previews, onGoTab }) {
  const primary = images.find((im) => im.is_primary) || images[0];
  const [activeId, setActiveId] = useState(primary?.id || null);
  const active = images.find((im) => im.id === activeId) || primary;
  const url = active ? previews[active.id] : null;
  return (
    <Card className="rounded-sm bg-[#FCFCFD] dark:bg-card border border-slate-300 dark:border-border shadow-[0_1px_2px_rgba(16,24,40,0.05),0_8px_24px_-12px_rgba(16,24,40,0.10)] dark:shadow-md dark:shadow-black/30 p-3 space-y-3" data-testid="pd-image-card">
      <div className="h-52 sm:h-64 md:h-auto md:aspect-square bg-muted/40 rounded-sm flex items-center justify-center overflow-hidden">
        {url ? (
          <img src={url} alt="" className="max-w-full max-h-full object-contain" data-testid="pd-main-image" />
        ) : (
          <div className="text-center text-muted-foreground">
            <ImageIcon className="w-10 h-10 mx-auto mb-2" />
            <div className="text-xs">Resim yok</div>
          </div>
        )}
      </div>
      {images.length > 1 && (
        <div className="grid grid-cols-4 gap-2">
          {images.slice(0, 8).map((im) => (
            <button
              key={im.id}
              type="button"
              data-testid={`pd-thumb-${im.id}`}
              onClick={() => setActiveId(im.id)}
              className={`aspect-square rounded-sm border overflow-hidden bg-muted/40 flex items-center justify-center transition-colors ${im.id === active?.id ? "border-primary" : "border-border hover:border-primary/50"}`}
            >
              {previews[im.id] ? (
                <img src={previews[im.id]} alt="" className="max-w-full max-h-full object-contain" />
              ) : <ImageIcon className="w-4 h-4 text-muted-foreground" />}
            </button>
          ))}
        </div>
      )}
      {images.length === 0 && (
        <Button variant="outline" size="sm" className="w-full rounded-sm text-xs" onClick={() => onGoTab("resimler")} data-testid="pd-go-images-btn">
          <Upload className="w-3.5 h-3.5 mr-1" /> Resimler sekmesinden yükle
        </Button>
      )}
    </Card>
  );
}

// ---- Sağ: stok bilgileri kartı (stock-summary + FAZ 2C availability REUSE) ----
function StockInfoCard({ productId, summary, warehouses, onGoTab, availRows = null }) {
  const [lastMove, setLastMove] = useState(null);
  useEffect(() => {
    api.get(`/products/${productId}/stock-movements`, { params: { limit: 1 } })
      .then((r) => setLastMove((r.data || [])[0] || null))
      .catch(() => {});
  }, [productId]);
  const defWh = warehouses.find((w) => w.is_default === true) || warehouses[0];
  const low = summary?.is_low;
  const avail = availRows ? aggregateForProduct(availRows, productId) : null;
  return (
    <Card className="rounded-sm bg-[#FCFCFD] dark:bg-card border border-slate-300 dark:border-border shadow-[0_1px_2px_rgba(16,24,40,0.05),0_8px_24px_-12px_rgba(16,24,40,0.10)] dark:shadow-md dark:shadow-black/30 p-4 space-y-3" data-testid="pd-stock-card">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[13px] font-semibold uppercase tracking-wider text-slate-800 dark:text-foreground flex items-center gap-1.5">
          <WarehouseIcon className="w-3.5 h-3.5" /> Stok Bilgileri
        </div>
        <Button variant="outline" size="sm" className="rounded-sm h-7 text-[10px] uppercase tracking-wider" onClick={() => onGoTab("stok")} data-testid="pd-stock-movements-btn">
          Stok Hareketleri
        </Button>
      </div>
      <div className={`rounded-sm border p-3 text-center ${low ? "border-rose-500/40 bg-rose-500/10" : "border-emerald-500/40 bg-emerald-500/10"}`}>
        <div className="text-[10px] uppercase tracking-wider font-medium text-slate-500 dark:text-muted-foreground">Toplam Stok</div>
        <div className={`text-2xl font-bold tabular-nums ${low ? "text-rose-500" : "text-emerald-500"}`} data-testid="pd-total-stock">
          {summary ? fmt(summary.total_quantity) : "—"}
        </div>
        <div className="text-[9px] text-muted-foreground">Adet</div>
        {availRows === null ? (
          <div className="text-[9px] text-amber-500 mt-1" data-testid="pd-avail-mini-error">Rezerve/yolda bilgisi alınamadı</div>
        ) : avail ? (
          <div className="text-[10px] font-mono text-slate-500 dark:text-muted-foreground mt-1" data-testid="pd-avail-mini">
            Rezerve <span className="text-amber-500 font-semibold">{fmt(avail.reserved)}</span>
            {" · "}Yolda <span className="text-sky-500 font-semibold">{fmt(avail.incoming)}</span>
            {" · "}Kullanılabilir <span className="text-emerald-500 font-semibold">{fmt(avail.available)}</span>
          </div>
        ) : null}
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-wide font-medium text-slate-500 dark:text-muted-foreground mb-1">Varsayılan Depo</div>
        <div className="rounded-sm border border-slate-300 dark:border-border bg-slate-100 dark:bg-muted/30 px-3 py-2 text-sm font-semibold" data-testid="pd-default-warehouse">
          {defWh ? defWh.name : "—"}
        </div>
      </div>
      <Button variant="outline" size="sm" className="w-full rounded-sm text-[10px] uppercase tracking-wider" onClick={() => onGoTab("stok")} data-testid="pd-stock-detail-btn">
        Stok Detayları
      </Button>
      {lastMove && (
        <div className="border-t border-border pt-2 flex items-center justify-between gap-2 text-[11px]" data-testid="pd-last-movement">
          <div>
            <div className="text-[9px] uppercase text-muted-foreground">Son Stok Hareketi</div>
            <div className="">{fmtDate(lastMove.created_at)}</div>
            <div className="text-muted-foreground">{(KIND_META[lastMove.kind] || KIND_META.manuel).label}{lastMove.warehouse_name ? ` · ${lastMove.warehouse_name}` : ""}</div>
          </div>
          <div className={`font-bold tabular-nums ${Number(lastMove.quantity_delta) >= 0 ? "text-emerald-500" : "text-rose-500"}`}>
            {Number(lastMove.quantity_delta) >= 0 ? "+" : ""}{fmt(lastMove.quantity_delta)}
          </div>
        </div>
      )}
    </Card>
  );
}

// ---- Alt: fiyat geçmişi kartı (mevcut sales-history / purchase-history REUSE) ----
function PriceHistoryCard({ productId, onGoTab }) {
  const [sub, setSub] = useState("sales");
  const [sales, setSales] = useState(null);
  const [purchases, setPurchases] = useState(null);
  useEffect(() => {
    api.get(`/products/${productId}/sales-history`)
      .then((r) => setSales((r.data || []).slice(0, 5)))
      .catch(() => setSales([]));
    api.get(`/products/${productId}/purchase-history`)
      .then((r) => setPurchases((r.data || []).slice(0, 5)))
      .catch(() => setPurchases([]));
  }, [productId]);

  const thCls = "text-left px-3 py-2 text-[11px] uppercase tracking-wide font-medium text-slate-500 dark:text-muted-foreground";
  const subBtn = (v, label, testId) => (
    <button
      type="button"
      data-testid={testId}
      onClick={() => setSub(v)}
      className={`px-3 py-1 text-[11px] font-semibold transition-colors ${sub === v ? "bg-primary text-primary-foreground" : "bg-transparent text-muted-foreground hover:bg-muted/40"}`}
    >
      {label}
    </button>
  );

  return (
    <Card className="rounded-sm bg-[#FCFCFD] dark:bg-card border border-slate-300 dark:border-border shadow-[0_1px_2px_rgba(16,24,40,0.05),0_8px_24px_-12px_rgba(16,24,40,0.10)] dark:shadow-md dark:shadow-black/30 p-4 space-y-3" data-testid="pd-price-history">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="text-[13px] font-semibold uppercase tracking-wider text-slate-800 dark:text-foreground">Fiyat Geçmişi</div>
          <div className="flex rounded-sm border border-border overflow-hidden">
            {subBtn("sales", "Son Satışlar", "pd-ph-tab-sales")}
            {subBtn("purchases", "Son Alışlar", "pd-ph-tab-purchases")}
          </div>
        </div>
        <Button variant="outline" size="sm" className="rounded-sm text-[10px] uppercase tracking-wider" onClick={() => onGoTab("gecmis")} data-testid="pd-ph-show-all">
          <FileText className="w-3.5 h-3.5 mr-1" /> Tüm Fiyat Geçmişini Gör
        </Button>
      </div>
      {sub === "sales" ? (
        sales === null ? (
          <div className="p-4 flex justify-center"><Loader2 className="w-4 h-4 animate-spin" /></div>
        ) : sales.length === 0 ? (
          <div className="p-4 text-xs text-muted-foreground text-center">Satış kaydı yok</div>
        ) : (
          <div className="overflow-x-auto max-w-full">
            <table className="w-full min-w-[520px]" data-testid="pd-ph-sales-table">
              <thead className="bg-muted/40 border-b border-border">
                <tr>
                  <th className={thCls}>Tarih</th>
                  <th className={thCls}>Müşteri</th>
                  <th className={`${thCls} text-right`}>Miktar</th>
                  <th className={`${thCls} text-right`}>Birim Fiyat</th>
                  <th className={thCls}>Para Birimi</th>
                  <th className={thCls}>Belge No</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {sales.map((r, i) => (
                  <tr key={i}>
                    <td className="px-3 py-1.5 text-xs">{fmtDate(r.date)}</td>
                    <td className="px-3 py-1.5 text-xs">{r.customer_name || "—"}</td>
                    <td className="px-3 py-1.5 text-xs text-right">{r.quantity}</td>
                    <td className="px-3 py-1.5 text-xs text-right">{fmt(r.unit_price)}</td>
                    <td className="px-3 py-1.5 text-xs">{(r.currency || "TRY") === "TRY" ? "TL" : r.currency}</td>
                    <td className="px-3 py-1.5 text-xs">{r.order_no || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : (
        purchases === null ? (
          <div className="p-4 flex justify-center"><Loader2 className="w-4 h-4 animate-spin" /></div>
        ) : purchases.length === 0 ? (
          <div className="p-4 text-xs text-muted-foreground text-center">Alış kaydı yok</div>
        ) : (
          <div className="overflow-x-auto max-w-full">
            <table className="w-full min-w-[520px]" data-testid="pd-ph-purchases-table">
              <thead className="bg-muted/40 border-b border-border">
                <tr>
                  <th className={thCls}>Tarih</th>
                  <th className={thCls}>Depo</th>
                  <th className={`${thCls} text-right`}>Miktar</th>
                  <th className={`${thCls} text-right`}>Birim Fiyat</th>
                  <th className={thCls}>Para Birimi</th>
                  <th className={thCls}>Belge No</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {purchases.map((r, i) => (
                  <tr key={i}>
                    <td className="px-3 py-1.5 text-xs">{fmtDate(r.created_at)}</td>
                    <td className="px-3 py-1.5 text-xs">{r.warehouse_name || "—"}</td>
                    <td className="px-3 py-1.5 text-xs text-right">{r.quantity_delta}</td>
                    <td className="px-3 py-1.5 text-xs text-right">{fmt(r.unit_price)}</td>
                    <td className="px-3 py-1.5 text-xs">{(r.currency || "TRY") === "TRY" ? "TL" : r.currency}</td>
                    <td className="px-3 py-1.5 text-xs">{r.reference_id || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </Card>
  );
}

// ---- Variants tab ---------------------------------------------------------
function VariantsTab({ productId, variants: rawVariants, onReload }) {
  const { can } = useAuth();
  const canStock = can("warehouse", "edit");  // SEC-003: stok değişimi backend'de warehouse.edit ister
  const navigate = useNavigate();
  // Kullanıcı isteği: her yerde önce renklere, sonra bedenlere göre sıralı gösterim
  const variants = useMemo(() => sortVariants(rawVariants || []), [rawVariants]);
  const [edits, setEdits] = useState({}); // id -> patch
  const [creatingOpen, setCreatingOpen] = useState(false);
  const [newVariant, setNewVariant] = useState({ sku: "", barcode: "", gtin: "", attributes: {}, purchase_price: 0, sales_price: 0 });
  const [attrKey, setAttrKey] = useState("beden");
  const [attrValue, setAttrValue] = useState("");
  const [variantSets, setVariantSets] = useState([]);
  // set_id -> [selected options] : her set için bağımsız çoklu seçim
  const [setPicks, setSetPicks] = useState({});
  const [genBarcodes, setGenBarcodes] = useState(false);
  const [creating, setCreating] = useState(false);
  const [labelOpen, setLabelOpen] = useState(false);

  useEffect(() => {
    if (creatingOpen) {
      api.get("/variant-sets").then(({ data }) =>
        setVariantSets((data || []).filter((s) => s.is_active !== false))
      ).catch(() => setVariantSets([]));
      setSetPicks({});
    }
  }, [creatingOpen]);

  // Kartezyen çarpım: seçili tüm setlerdeki seçenekleri kombinle.
  // Attribute key olarak set adını normalize eder (aynı tipteki 2 set çakışmasın).
  const combinations = useMemo(() => {
    const picks = Object.entries(setPicks)
      .filter(([, opts]) => opts && opts.length > 0)
      .map(([sid, opts]) => {
        const s = variantSets.find((x) => x.id === sid);
        // Aynı variant_type'ta birden fazla set seçilmişse çakışmayı önlemek için
        // set adını kullan. Tek setse type'ı kullan (temiz "beden"/"renk").
        const sameTypeSets = variantSets.filter((x) => x.variant_type === s?.variant_type);
        const activeSameType = Object.keys(setPicks).filter((k) => {
          const other = variantSets.find((x) => x.id === k);
          return other?.variant_type === s?.variant_type && (setPicks[k] || []).length > 0;
        });
        const key = (activeSameType.length > 1 || sameTypeSets.length === 0)
          ? (s?.name || "attr").toLowerCase().replace(/[^a-z0-9ığşöçü]+/g, "_").replace(/^_|_$/g, "")
          : (s?.variant_type || "attr");
        return { key, options: opts };
      });
    if (picks.length === 0) return [];
    let result = [{}];
    picks.forEach(({ key, options }) => {
      const next = [];
      result.forEach((base) => {
        options.forEach((o) => next.push({ ...base, [key]: o }));
      });
      result = next;
    });
    return result;
  }, [setPicks, variantSets]);

  const patchLocal = (vid, key, val) => setEdits((p) => ({ ...p, [vid]: { ...(p[vid] || {}), [key]: val } }));
  const getVal = (v, key) => (edits[v.id] && edits[v.id][key] !== undefined) ? edits[v.id][key] : v[key];

  const dirtyCount = Object.keys(edits).length;
  const [savingAll, setSavingAll] = useState(false);

  const saveAll = async () => {
    const ids = Object.keys(edits);
    if (ids.length === 0) {
      toast.info("Kaydedilecek değişiklik yok");
      return;
    }
    setSavingAll(true);
    let ok = 0;
    let fail = 0;
    for (const vid of ids) {
      try {
        await api.patch(`/variants/${vid}`, edits[vid]);
        ok += 1;
      } catch (e) {
        fail += 1;
        const v = variants.find((x) => x.id === vid);
        const label = v ? formatVariantLabel(v.attributes, v.sku || vid.slice(0, 8)) : vid.slice(0, 8);
        toast.error(`${label}: ${formatApiError(e)}`);
      }
    }
    setEdits({});
    setSavingAll(false);
    if (ok > 0) toast.success(`${ok} varyant güncellendi${fail > 0 ? ` · ${fail} hata` : ""}`);
    onReload?.();
  };

  const remove = async (v) => {
    const label = formatVariantLabel(v.attributes, v.sku || v.id.slice(0, 8));
    if (!window.confirm(`${label} varyantı silinsin mi?`)) return;
    try {
      await api.delete(`/variants/${v.id}`);
      toast.success("Silindi");
      onReload?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const create = async () => {
    // Attribute kombinasyonu parmak izi (SKU'dan bağımsız duplicate tespiti)
    const comboKey = (attrs) => Object.entries(attrs || {})
      .map(([k, v]) => [String(k).trim().toLowerCase(), String(v).trim().toLowerCase()])
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([k, v]) => `${k}=${v}`).join("|");
    const existingKeys = new Set((variants || []).map((v) => comboKey(v.attributes)));
    // Eğer setPicks'te seçim varsa, kartezyen çarpımdan çoklu varyant üret
    if (combinations.length > 0) {
      if (creating) return;
      setCreating(true);
      try {
        // İstek içi dedupe + mevcut varyantlarla çakışanları ele
        const seen = new Set();
        const fresh = [];
        let skippedExisting = 0;
        for (const combo of combinations) {
          const attrs = { ...newVariant.attributes, ...combo };
          const key = comboKey(attrs);
          if (seen.has(key)) continue;
          seen.add(key);
          if (existingKeys.has(key)) { skippedExisting += 1; continue; }
          fresh.push(attrs);
        }
        if (fresh.length === 0) {
          toast.warning("Seçilen varyantlar zaten mevcut");
          return;
        }
        let count = 0, dup = 0;
        for (const attrs of fresh) {
          try {
            await api.post(`/products/${productId}/variants`, {
              sku: "",
              barcode: "",
              gtin: "",
              attributes: attrs,
              cost: Number(newVariant.purchase_price) || 0,
              purchase_price: Number(newVariant.purchase_price) || 0,
              sales_price: Number(newVariant.sales_price) || 0,
              stock_qty: 0,
            });
            count += 1;
          } catch (e) {
            if (e?.response?.status === 409) dup += 1;
            else throw e;
          }
        }
        const skippedTotal = skippedExisting + dup;
        if (count > 0) {
          toast.success(`${count} varyant oluşturuldu${skippedTotal > 0 ? ` · ${skippedTotal} tanesi zaten mevcuttu, atlandı` : ""}`);
        } else {
          toast.warning("Seçilen varyantlar zaten mevcut");
        }
        if (count > 0) {
          setCreatingOpen(false);
          setNewVariant({ sku: "", barcode: "", gtin: "", attributes: {}, purchase_price: 0, sales_price: 0 });
          setSetPicks({});
          onReload?.();
        }
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setCreating(false); }
      return;
    }
    // Tekli varyant (manuel attributes)
    if (!newVariant.attributes || Object.keys(newVariant.attributes).length === 0) {
      toast.error("En az bir özellik ekleyin (setten seçin veya manuel girin)");
      return;
    }
    if (existingKeys.has(comboKey(newVariant.attributes))) {
      toast.warning("Bu varyant kombinasyonu zaten mevcut");
      return;
    }
    if (creating) return;
    setCreating(true);
    try {
      await api.post(`/products/${productId}/variants`, {
        sku: newVariant.sku || "",
        barcode: newVariant.barcode || "",
        gtin: newVariant.gtin || "",
        attributes: newVariant.attributes,
        cost: Number(newVariant.purchase_price) || 0,
        purchase_price: Number(newVariant.purchase_price) || 0,
        sales_price: Number(newVariant.sales_price) || 0,
        stock_qty: 0,
      });
      toast.success("Varyant eklendi");
      setCreatingOpen(false);
      setNewVariant({ sku: "", barcode: "", gtin: "", attributes: {}, purchase_price: 0, sales_price: 0 });
      onReload?.();
    } catch (e) {
      if (e?.response?.status === 409) toast.warning("Bu varyant kombinasyonu zaten mevcut");
      else toast.error(formatApiError(e));
    }
    finally { setCreating(false); }
  };

  const generateBarcodes = async () => {
    setGenBarcodes(true);
    try {
      const { data } = await api.post(`/products/${productId}/variants/generate-barcodes`, {});
      toast.success(`${data.generated || 0} barkod üretildi`);
      onReload?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setGenBarcodes(false); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-[11px] uppercase tracking-wide font-medium text-slate-500 dark:text-muted-foreground">
          {variants.length} varyant · Değişiklikleri toplu olarak "Tümünü Kaydet" ile onaylayın
          {dirtyCount > 0 && (
            <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-amber-500/15 text-amber-500 font-bold">
              · {dirtyCount} değişiklik bekliyor
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {variants.length > 0 && (
            <Button
              data-testid="save-all-variants-top"
              size="sm"
              onClick={saveAll}
              disabled={savingAll || dirtyCount === 0}
              className="rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-40"
              title="Değiştirilen tüm varyantları kaydet"
            >
              {savingAll ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />}
              Tümünü Kaydet{dirtyCount > 0 ? ` (${dirtyCount})` : ""}
            </Button>
          )}
          {variants.length > 0 && variants.some((v) => !v.barcode) && (
            <Button
              data-testid="generate-barcodes-btn"
              size="sm"
              variant="outline"
              onClick={generateBarcodes}
              disabled={genBarcodes}
              className="rounded-sm border-emerald-700 text-emerald-400 hover:bg-emerald-950/40 uppercase tracking-wider text-xs font-semibold"
              title="Barkodu olmayan tüm varyantlar için EAN-13 uyumlu barkod üretir"
            >
              {genBarcodes ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
              Barkod Üret (EAN-13)
            </Button>
          )}
          {variants.length > 0 && variants.some((v) => v.barcode || v.sku) && (
            <Button
              data-testid="print-labels-btn"
              size="sm"
              variant="outline"
              onClick={() => setLabelOpen(true)}
              className="rounded-sm border-blue-700 text-blue-400 hover:bg-blue-950/40 uppercase tracking-wider text-xs font-semibold"
              title="Barkod etiketlerini PDF olarak yazdır"
            >
              <FileText className="w-3.5 h-3.5 mr-1" /> Etiket Yazdır
            </Button>
          )}
          <Button data-testid="add-variant-btn" size="sm" onClick={() => setCreatingOpen(true)} className="rounded-sm">
            <Plus className="w-3.5 h-3.5 mr-1" /> Varyant Ekle
          </Button>
        </div>
      </div>

      {variants.length === 0 ? (
        <Card className="rounded-sm border-dashed p-8 text-center text-sm text-muted-foreground">
          Bu ürüne henüz varyant eklenmemiş.
        </Card>
      ) : (
        <div className="border border-border rounded-sm overflow-x-auto">
          <table className="w-full text-sm" data-testid="variants-table">
            <thead className="bg-muted/40 border-b border-border">
              <tr className="text-[11px] uppercase tracking-wide font-medium text-slate-500 dark:text-muted-foreground">
                <th className="text-left px-3 py-2">Renk · Beden</th>
                <th className="text-left px-3 py-2">Barkod</th>
                <th className="text-left px-3 py-2">GTIN</th>
                <th className="text-right px-3 py-2">Alış</th>
                <th className="text-right px-3 py-2">Satış</th>
                <th className="text-right px-3 py-2">Stok</th>
                <th className="px-3 py-2 w-16">Sil</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {variants.map((v) => {
                const attrLabel = formatVariantLabel(v.attributes);
                const dirty = !!edits[v.id];
                return (
                  <tr key={v.id} data-testid={`variant-row-${v.id}`} className={dirty ? "bg-amber-500/5" : ""}>
                    <td className="px-3 py-1.5">
                      <div
                        className="text-sm font-semibold text-emerald-500 dark:text-emerald-400"
                        data-testid={`v-${v.id}-attrs`}
                      >
                        {attrLabel}
                      </div>
                    </td>
                    <td className="px-3 py-1.5">
                      <Input
                        data-testid={`v-${v.id}-barcode`}
                        value={getVal(v, "barcode") || ""}
                        onChange={(e) => patchLocal(v.id, "barcode", e.target.value)}
                        className="rounded-sm h-8 text-xs w-36"
                      />
                    </td>
                    <td className="px-3 py-1.5">
                      <Input
                        data-testid={`v-${v.id}-gtin`}
                        value={getVal(v, "gtin") || ""}
                        onChange={(e) => patchLocal(v.id, "gtin", e.target.value)}
                        placeholder="—"
                        className="rounded-sm h-8 text-xs w-36"
                      />
                    </td>
                    <td className="px-3 py-1.5">
                      <Input
                        data-testid={`v-${v.id}-purchase`}
                        type="number" step="0.01" min="0"
                        value={getVal(v, "purchase_price") ?? v.cost ?? 0}
                        onChange={(e) => patchLocal(v.id, "purchase_price", Number(e.target.value) || 0)}
                        className="rounded-sm h-8 text-xs w-24 text-right"
                      />
                    </td>
                    <td className="px-3 py-1.5">
                      <Input
                        data-testid={`v-${v.id}-sales`}
                        type="number" step="0.01" min="0"
                        value={getVal(v, "sales_price") ?? 0}
                        onChange={(e) => patchLocal(v.id, "sales_price", Number(e.target.value) || 0)}
                        className="rounded-sm h-8 text-xs w-24 text-right"
                      />
                    </td>
                    <td className="px-3 py-1.5">
                      <Input
                        data-testid={`v-${v.id}-stock`}
                        type="number" step="1" min="0"
                        value={getVal(v, "stock_qty") ?? 0}
                        onChange={(e) => patchLocal(v.id, "stock_qty", Number(e.target.value) || 0)}
                        disabled={!canStock}
                        title={!canStock ? "Stok değiştirme yetkiniz yok (warehouse.edit)" : undefined}
                        className="rounded-sm h-8 text-xs w-20 text-right"
                      />
                    </td>
                    <td className="px-3 py-1.5">
                      <div className="flex items-center justify-end">
                        <button
                          data-testid={`v-${v.id}-delete`}
                          onClick={() => remove(v)}
                          className="text-muted-foreground hover:text-rose-500 p-1"
                          title="Sil"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Alt bar: Tümünü Kaydet */}
      {variants.length > 0 && (
        <div className="flex items-center justify-end pt-2 border-t border-border/40">
          <Button
            data-testid="save-all-variants-bottom"
            onClick={saveAll}
            disabled={savingAll || dirtyCount === 0}
            className="rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-40"
          >
            {savingAll ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            Tümünü Kaydet{dirtyCount > 0 ? ` (${dirtyCount})` : ""}
          </Button>
        </div>
      )}

      <Dialog open={creatingOpen} onOpenChange={setCreatingOpen}>
        <DialogContent className="rounded-sm w-[95vw] max-w-[95vw] sm:max-w-2xl md:max-w-3xl lg:max-w-5xl max-h-[92vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-5 pb-3 shrink-0 border-b border-slate-800">
            <DialogTitle>Yeni Varyant</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 px-6 py-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* SOL SÜTUN — Temel bilgiler */}
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-xs">SKU</Label>
                    <Input value={newVariant.sku} onChange={(e) => setNewVariant((p) => ({ ...p, sku: e.target.value }))} className="rounded-sm mt-1 bg-slate-50 dark:bg-background border-slate-300 dark:border-input" placeholder="Boş bırakabilirsiniz" />
                  </div>
                  <div>
                    <Label className="text-xs">Barkod</Label>
                    <Input value={newVariant.barcode} onChange={(e) => setNewVariant((p) => ({ ...p, barcode: e.target.value }))} className="rounded-sm mt-1 bg-slate-50 dark:bg-background border-slate-300 dark:border-input" />
                  </div>
                  <div>
                    <Label className="text-xs">GTIN</Label>
                    <Input value={newVariant.gtin} onChange={(e) => setNewVariant((p) => ({ ...p, gtin: e.target.value }))} className="rounded-sm mt-1 bg-slate-50 dark:bg-background border-slate-300 dark:border-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Alış Fiyatı</Label>
                    <Input type="number" step="0.01" min="0"
                      value={newVariant.purchase_price}
                      onChange={(e) => setNewVariant((p) => ({ ...p, purchase_price: e.target.value }))}
                      className="rounded-sm mt-1 font-medium bg-slate-50 dark:bg-background border-slate-300 dark:border-input" />
                  </div>
                  <div>
                    <Label className="text-xs">Satış Fiyatı</Label>
                    <Input type="number" step="0.01" min="0"
                      value={newVariant.sales_price}
                      onChange={(e) => setNewVariant((p) => ({ ...p, sales_price: e.target.value }))}
                      className="rounded-sm mt-1 font-medium bg-slate-50 dark:bg-background border-slate-300 dark:border-input" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Özellikler (örn: beden=M)</Label>
                  <div className="flex gap-2 mt-1">
                    <Input placeholder="anahtar" value={attrKey} onChange={(e) => setAttrKey(e.target.value)} className="rounded-sm w-32" />
                    <Input placeholder="değer" value={attrValue} onChange={(e) => setAttrValue(e.target.value)} className="rounded-sm flex-1" />
                    <Button size="sm" onClick={() => {
                      if (!attrKey || !attrValue) return;
                      setNewVariant((p) => ({ ...p, attributes: { ...p.attributes, [attrKey]: attrValue } }));
                      setAttrValue("");
                    }} className="rounded-sm">Ekle</Button>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {Object.entries(newVariant.attributes).map(([k, v]) => (
                      <span key={k} className="inline-flex items-center gap-1 bg-muted border border-border rounded-sm px-2 py-0.5 text-xs">
                        <span className="text-muted-foreground">{k}=</span>{v}
                        <button onClick={() => setNewVariant((p) => {
                          const a = { ...p.attributes }; delete a[k]; return { ...p, attributes: a };
                        })} className="text-muted-foreground hover:text-rose-500">
                          <X className="w-2.5 h-2.5" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* SAĞ SÜTUN — Varyant setleri (geniş ekranda yan yana) */}
              {variantSets.length > 0 && (
                <div className="border border-border rounded-sm bg-muted/20 p-3 space-y-2 lg:mt-0">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                      Varyant Setinden Seç <span className="text-[10px] normal-case">— çoklu seçim</span>
                    </Label>
                    <button
                      type="button"
                      data-testid="manage-variant-sets-btn"
                      onClick={() => {
                        setCreatingOpen(false);
                        setTimeout(() => navigate("/varyantlar"), 50);
                      }}
                      className="text-[10px] uppercase tracking-widest text-blue-500 hover:text-blue-400 bg-transparent border-0 p-0 cursor-pointer"
                    >
                      Setleri yönet →
                    </button>
                  </div>
                  {["size", "color", "other"].map((type) => {
                    const inType = variantSets.filter((s) => s.variant_type === type);
                    if (inType.length === 0) return null;
                    const label = type === "size" ? "Beden" : type === "color" ? "Renk" : "Diğer";
                    return (
                      <div key={type} className="space-y-1.5">
                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
                        <div className="flex flex-wrap gap-1.5">
                          {inType.map((s) => {
                            const setSelected = setPicks[s.id] || [];
                            return (
                              <div
                                key={s.id}
                                data-testid={`variant-set-group-${s.id}`}
                                className={`border rounded-sm p-2 bg-background ${
                                  setSelected.length > 0 ? "border-blue-700" : "border-border"
                                }`}
                              >
                                <div className="text-[11px] font-semibold text-foreground mb-1 flex items-center gap-1.5">
                                  <span>{s.name}</span>
                                  {setSelected.length > 0 && (
                                    <span className="text-[9px] text-blue-400">
                                      ({setSelected.length} seçili)
                                    </span>
                                  )}
                                </div>
                                <div className="flex flex-wrap gap-1">
                                  {(s.options || []).map((opt) => {
                                    const selected = setSelected.includes(opt);
                                    return (
                                      <button
                                        key={opt}
                                        type="button"
                                        data-testid={`variant-opt-${s.id}-${opt}`}
                                        onClick={() => {
                                          setSetPicks((prev) => {
                                            const cur = prev[s.id] || [];
                                            const next = cur.includes(opt)
                                              ? cur.filter((x) => x !== opt)
                                              : [...cur, opt];
                                            return { ...prev, [s.id]: next };
                                          });
                                        }}
                                        className={`text-[11px] px-2 py-0.5 border rounded-sm transition-colors ${
                                          selected
                                            ? "border-blue-500 bg-blue-950/40 text-blue-300"
                                            : "border-border bg-background text-slate-300 hover:border-blue-700 hover:bg-blue-950/20"
                                        }`}
                                      >
                                        {opt}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                  {combinations.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-border text-[11px] text-slate-300 flex items-center justify-between">
                      <span>
                        → <b className="text-blue-400 text-sm">{combinations.length}</b> varyant üretilecek
                      </span>
                      <button
                        type="button"
                        onClick={() => setSetPicks({})}
                        className="text-[10px] uppercase tracking-widest text-slate-500 hover:text-slate-300"
                      >
                        Seçimi temizle
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="px-6 py-3 border-t border-slate-800 shrink-0 bg-slate-950/60">
            <Button variant="ghost" onClick={() => setCreatingOpen(false)} className="rounded-sm">İptal</Button>
            <Button data-testid="create-variant-btn" onClick={create} disabled={creating} className="rounded-sm bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-40">
              {creating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />} Ekle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BarcodeLabelDialog
        open={labelOpen}
        onOpenChange={setLabelOpen}
        productId={productId}
        variants={variants}
      />
    </div>
  );
}

// ---- Stock tab ------------------------------------------------------------
function StockTab({ product, productId, variants, warehouses, summary, onReload, availRows = null }) {
  const { can } = useAuth();  // SEC-003: stok opsiyonu products.edit, siparişe dönüştür orders.add
  const [dialogOpen, setDialogOpen] = useState(false);
  // FAZ SO1 — stok opsiyonları
  const [optionOpen, setOptionOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [cancellingOpt, setCancellingOpt] = useState(null);
  const [convTarget, setConvTarget] = useState(null);
  const [convQty, setConvQty] = useState("");
  const [convPrice, setConvPrice] = useState("");
  const [convTax, setConvTax] = useState("20");
  const [convSaving, setConvSaving] = useState(false);
  const openConvert = (o) => {
    setConvTarget(o);
    setConvQty(String(o.quantity).replace(".", ","));
    const sp = Number(product?.sale_price || product?.base_price || 0);
    setConvPrice(sp ? String(sp).replace(".", ",") : "");
    setConvTax(String(Number(product?.tax_rate ?? 20)));
  };
  const submitConvert = async () => {
    if (!convTarget) return;
    const qty = parseInt(String(convQty).replace(",", "."), 10);
    const price = Number(String(convPrice).replace(/\./g, "").replace(",", ".")) || 0;
    if (!qty || qty <= 0) { toast.error("Miktar 0'dan büyük olmalı"); return; }
    if (qty > convTarget.quantity) { toast.error(`Miktar opsiyonu aşamaz (maks ${convTarget.quantity})`); return; }
    setConvSaving(true);
    try {
      const { data } = await api.post(`/inventory/stock-options/${convTarget.id}/convert-to-order`, {
        quantity: qty, unit_price: price, tax_rate: Number(convTax || 0),
      });
      toast.success(`${data.order_no} oluşturuldu — ${qty} adet opsiyon rezervi siparişe transfer edildi`);
      setConvTarget(null);
      loadOptions();
      onReload?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setConvSaving(false); }
  };
  const loadOptions = async () => {
    try {
      const { data } = await api.get("/inventory/stock-options", { params: { product_id: productId } });
      setOptions((data || []).filter((o) => o.status === "active"));
    } catch { /* sessiz */ }
  };
  useEffect(() => { loadOptions(); /* eslint-disable-next-line */ }, [productId]);
  const cancelOption = async (o) => {
    if (!window.confirm(`${o.customer_name} için ${o.quantity} adetlik opsiyon serbest bırakılacak. Devam?`)) return;
    setCancellingOpt(o.id);
    try {
      await api.post(`/inventory/stock-options/${o.id}/cancel`);
      toast.success("Opsiyon serbest bırakıldı");
      loadOptions();
      onReload?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCancellingOpt(null); }
  };
  const [extractOpen, setExtractOpen] = useState(false);
  const [form, setForm] = useState({
    variant_id: "", warehouse_id: "", quantity_delta: 1, kind: "manuel", reason: "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.warehouse_id) { toast.error("Depo seçin"); return; }
    if (!form.quantity_delta || Number(form.quantity_delta) === 0) { toast.error("Miktar 0 olamaz"); return; }
    setSaving(true);
    try {
      await api.post("/inventory/stock/movement", {
        product_id: productId,
        variant_id: form.variant_id || "",
        warehouse_id: form.warehouse_id,
        quantity_delta: Number(form.quantity_delta),
        kind: form.kind,
        reason: form.reason || "",
      });
      toast.success("Stok hareketi kaydedildi");
      setDialogOpen(false);
      setForm({ variant_id: "", warehouse_id: "", quantity_delta: 1, kind: "manuel", reason: "" });
      onReload?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      {/* FAZ 2C — canonical availability paneli (tek istek, /inventory/stock-availability) */}
      <StockAvailabilityPanel
        productId={productId}
        rows={availRows}
        error={availRows === null}
        variants={variants}
        warehouses={warehouses}
        summary={summary}
      />

      {/* FAZ SO1 — Aktif stok opsiyonları */}
      <div className="rounded-sm border border-border bg-card" data-testid="stock-options-card">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-border">
          <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Stok Opsiyonları</div>
          <Button disabled={!(can("products", "edit"))} title={!(can("products", "edit")) ? "Stok opsiyonu yetkiniz yok (products.edit)" : undefined} size="sm" data-testid="stock-option-create-btn" onClick={() => setOptionOpen(true)}
            className="rounded-sm bg-sky-600 hover:bg-sky-500 text-white h-7 text-xs">
            Opsiyonla
          </Button>
        </div>
        {options.length === 0 ? (
          <div className="px-4 py-4 text-xs text-muted-foreground">Aktif opsiyon yok.</div>
        ) : (
          <div className="divide-y divide-border/60">
            {options.map((o) => (
              <div key={o.id} className="flex items-center justify-between gap-3 px-4 py-2.5 text-xs" data-testid={`stock-option-row-${o.id}`}>
                <div className="min-w-0">
                  <div className="text-sm text-foreground font-medium truncate">{o.customer_name}</div>
                  <div className="text-[10px] font-mono text-muted-foreground truncate">
                    {o.kind === "reserve" ? "REZERVE" : "OPSİYON"} · {o.variant_sku || "Genel"} · {o.warehouse_name}{o.kind === "reserve" ? "" : ` · Bitiş: ${o.expires_at}`}
                    {o.note ? ` · ${o.note}` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="font-mono text-foreground tabular-nums font-semibold">{Number(o.quantity).toLocaleString("tr-TR", { maximumFractionDigits: 3 })}</span>
                  {o.is_expired ? (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-sm border border-amber-500/40 bg-amber-500/15 text-amber-400" data-testid={`stock-option-expired-${o.id}`}>Süresi Doldu</span>
                  ) : (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-sm border border-emerald-500/40 bg-emerald-500/15 text-emerald-400">Aktif</span>
                  )}
                  <Button disabled={!(can("orders", "add"))} title={!(can("orders", "add")) ? "Sipariş oluşturma yetkiniz yok (orders.add)" : undefined} size="sm" variant="ghost" onClick={() => openConvert(o)}
                    data-testid={`stock-option-convert-${o.id}`}
                    className="h-6 px-2 text-[10px] text-sky-400 hover:text-sky-300 hover:bg-sky-950/40">
                    Siparişe Dönüştür
                  </Button>
                  <Button size="sm" variant="ghost" disabled={(cancellingOpt === o.id) || !(can("products", "edit"))} title={!(can("products", "edit")) ? "Stok opsiyonu yetkiniz yok (products.edit)" : undefined} onClick={() => cancelOption(o)}
                    data-testid={`stock-option-cancel-${o.id}`}
                    className="h-6 px-2 text-[10px] text-slate-500 hover:text-red-400 hover:bg-red-950/30">
                    Serbest Bırak
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* FAZ SO2 — Siparişe dönüştür dialogu */}
      <Dialog open={!!convTarget} onOpenChange={(v) => { if (!v) setConvTarget(null); }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-w-sm" data-testid="so-convert-dialog">
          <DialogHeader>
            <DialogTitle className="text-slate-100">Siparişe Dönüştür</DialogTitle>
            <DialogDescription className="text-slate-500 text-xs">
              {convTarget?.customer_name} · {convTarget?.variant_sku || "Genel"} · {convTarget?.warehouse_name}.
              Rezerv siparişe transfer edilir; kullanılabilir stok değişmez.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Miktar (maks {convTarget?.quantity}) *</Label>
              <Input data-testid="so-convert-qty" type="text" inputMode="numeric" value={convQty} onChange={(e) => setConvQty(e.target.value)} className="bg-slate-950 border-slate-700 rounded-sm h-9 font-mono" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Birim Fiyat (TL)</Label>
                <Input data-testid="so-convert-price" type="text" inputMode="decimal" value={convPrice} onChange={(e) => setConvPrice(e.target.value)} className="bg-slate-950 border-slate-700 rounded-sm h-9 font-mono" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">KDV %</Label>
                <Input data-testid="so-convert-tax" type="text" inputMode="numeric" value={convTax} onChange={(e) => setConvTax(e.target.value)} className="bg-slate-950 border-slate-700 rounded-sm h-9 font-mono" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConvTarget(null)} className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm" data-testid="so-convert-cancel">İptal</Button>
            <Button onClick={submitConvert} disabled={convSaving} className="bg-sky-600 hover:bg-sky-500 rounded-sm" data-testid="so-convert-submit">
              {convSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Sipariş Oluştur
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <StockOptionDialog
        open={optionOpen}
        onOpenChange={setOptionOpen}
        initialProduct={{ id: productId, name: summary?.name || "" }}
        onSuccess={() => { loadOptions(); onReload?.(); }}
      />

      <div className="flex items-center gap-2">
        <Button
          data-testid="stock-add-movement-btn"
          onClick={() => { setForm((p) => ({ ...p, kind: "manuel", quantity_delta: 1 })); setDialogOpen(true); }}
          className="rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white"
        >
          <ArrowUpCircle className="w-4 h-4 mr-1" /> Manuel Giriş / Çıkış
        </Button>
        <Button
          data-testid="stock-extract-btn"
          onClick={() => setExtractOpen(true)}
          variant="outline" className="rounded-sm"
        >
          <FileSpreadsheet className="w-4 h-4 mr-1" /> Stok Ekstresi
        </Button>
      </div>

      {/* Manuel movement dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle>Manuel Stok Hareketi</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Varyant (opsiyonel)</Label>
                <Select value={form.variant_id || "none"} onValueChange={(v) => setForm((p) => ({ ...p, variant_id: v === "none" ? "" : v }))}>
                  <SelectTrigger data-testid="movement-variant" className="rounded-sm mt-1 bg-slate-50 dark:bg-background border-slate-300 dark:border-input"><SelectValue placeholder="Ürün geneli" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— ürün geneli —</SelectItem>
                    {sortVariants(variants).map((v) => {
                      const label = formatVariantLabel(v.attributes, v.sku || v.id.slice(0, 8));
                      return <SelectItem key={v.id} value={v.id}>{label}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Depo *</Label>
                <Select value={form.warehouse_id} onValueChange={(v) => setForm((p) => ({ ...p, warehouse_id: v }))}>
                  <SelectTrigger data-testid="movement-warehouse" className="rounded-sm mt-1 bg-slate-50 dark:bg-background border-slate-300 dark:border-input"><SelectValue placeholder="Seç" /></SelectTrigger>
                  <SelectContent>
                    {warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.code} · {w.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Miktar (+giriş / -çıkış) *</Label>
                <Input
                  data-testid="movement-qty"
                  type="number" step="0.01"
                  value={form.quantity_delta}
                  onChange={(e) => setForm((p) => ({ ...p, quantity_delta: e.target.value }))}
                  className="rounded-sm mt-1 font-medium bg-slate-50 dark:bg-background border-slate-300 dark:border-input"
                />
              </div>
              <div>
                <Label className="text-xs">Hareket Tipi</Label>
                <Select value={form.kind} onValueChange={(v) => setForm((p) => ({ ...p, kind: v }))}>
                  <SelectTrigger className="rounded-sm mt-1 bg-slate-50 dark:bg-background border-slate-300 dark:border-input"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manuel">Manuel</SelectItem>
                    <SelectItem value="purchase">Alış</SelectItem>
                    <SelectItem value="sale">Satış</SelectItem>
                    <SelectItem value="production">Üretim</SelectItem>
                    <SelectItem value="shipment">Sevkiyat</SelectItem>
                    <SelectItem value="fire">Fire</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Sebep / Açıklama</Label>
              <Textarea
                data-testid="movement-reason"
                rows={2}
                value={form.reason}
                onChange={(e) => setForm((p) => ({ ...p, reason: e.target.value }))}
                className="rounded-sm mt-1 text-sm"
                placeholder="Örn: Sayım farkı, üretim iadesi vs."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)} className="rounded-sm">İptal</Button>
            <Button data-testid="movement-submit" onClick={submit} disabled={saving} className="rounded-sm">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Extract dialog */}
      <StockExtractDialog
        open={extractOpen}
        onOpenChange={setExtractOpen}
        productId={productId}
        variants={variants}
      />
    </div>
  );
}

// ---- Stock extract dialog (movements + xlsx) ------------------------------
function StockExtractDialog({ open, onOpenChange, productId, variants = [] }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [kindFilter, setKindFilter] = useState("all");

  const variantMap = useMemo(() => {
    const m = new Map();
    variants.forEach((v) => m.set(v.id, v));
    return m;
  }, [variants]);

  const variantLabel = (r) => {
    const v = variantMap.get(r.variant_id);
    if (v) return formatVariantLabel(v.attributes, r.variant_sku || "—");
    return r.variant_sku || "—";
  };

  const load = async () => {
    setLoading(true);
    try {
      const url = `/products/${productId}/stock-movements${kindFilter !== "all" ? `?kind=${kindFilter}` : ""}`;
      const { data } = await api.get(url);
      setRows(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); }, [open, kindFilter]); // eslint-disable-line

  const downloadXlsx = async () => {
    try {
      const url = `${API}/products/${productId}/stock-movements/export.xlsx${kindFilter !== "all" ? `?kind=${kindFilter}` : ""}`;
      const res = await api.get(url.replace(API, ""), { responseType: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(new Blob([res.data]));
      link.download = `stok_ekstresi_${productId.slice(0, 8)}.xlsx`;
      document.body.appendChild(link); link.click(); link.remove();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-4xl" data-testid="stock-extract-dialog">
        <DialogHeader>
          <DialogTitle>Stok Ekstresi (Hareketler)</DialogTitle>
        </DialogHeader>
        <div className="flex items-center justify-between gap-2 mb-3">
          <Select value={kindFilter} onValueChange={setKindFilter}>
            <SelectTrigger className="rounded-sm w-48"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm hareketler</SelectItem>
              <SelectItem value="manuel">Manuel</SelectItem>
              <SelectItem value="purchase">Alış</SelectItem>
              <SelectItem value="sale">Satış</SelectItem>
              <SelectItem value="production">Üretim</SelectItem>
              <SelectItem value="shipment">Sevkiyat</SelectItem>
              <SelectItem value="fire">Fire</SelectItem>
            </SelectContent>
          </Select>
          <Button
            data-testid="stock-extract-download"
            onClick={downloadXlsx}
            className="rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white"
          >
            <Download className="w-4 h-4 mr-1" /> Excel İndir
          </Button>
        </div>
        <div className="max-h-96 overflow-auto border border-border rounded-sm">
          {loading ? (
            <div className="p-8 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-sm text-muted-foreground text-center">Hareket kaydı yok</div>
          ) : (
            <table className="w-full text-sm" data-testid="stock-movements-table">
              <thead className="bg-muted/40 border-b border-border sticky top-0">
                <tr className="text-[11px] uppercase tracking-wide font-medium text-slate-500 dark:text-muted-foreground">
                  <th className="text-left px-2 py-2">Tarih</th>
                  <th className="text-left px-2 py-2">Tip</th>
                  <th className="text-left px-2 py-2">Depo</th>
                  <th className="text-left px-2 py-2">Varyant</th>
                  <th className="text-right px-2 py-2">Miktar</th>
                  <th className="text-left px-2 py-2">Sebep</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((r) => {
                  const meta = KIND_META[r.kind] || KIND_META.manuel;
                  const Icon = meta.icon;
                  return (
                    <tr key={r.id}>
                      <td className="px-2 py-1.5 text-xs">{fmtDate(r.created_at)}</td>
                      <td className={`px-2 py-1.5 text-xs font-semibold ${meta.color}`}>
                        <span className="inline-flex items-center gap-1"><Icon className="w-3 h-3" /> {meta.label}</span>
                      </td>
                      <td className="px-2 py-1.5 text-xs">{r.warehouse_name}</td>
                      <td className="px-2 py-1.5 text-xs font-semibold text-emerald-500 dark:text-emerald-400">
                        {variantLabel(r)}
                      </td>
                      <td className={`px-2 py-1.5 text-right font-bold ${r.quantity_delta > 0 ? "text-emerald-500" : "text-rose-500"}`}>
                        {r.quantity_delta > 0 ? "+" : ""}{fmt(r.quantity_delta)}
                      </td>
                      <td className="px-2 py-1.5 text-xs text-muted-foreground">{r.reason || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-sm">Kapat</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---- History tab (accordions) ---------------------------------------------
function HistoryTab({ productId, variants = [] }) {
  return (
    <div className="space-y-3">
      <AccordionSection title="Önceki Satışlar" endpoint={`/products/${productId}/sales-history`} testId="history-sales" kind="sales" variants={variants} />
      <AccordionSection title="Önceki Manuel Giriş / Çıkışlar" endpoint={`/products/${productId}/manual-history`} testId="history-manual" kind="manual" variants={variants} />
      <AccordionSection title="Önceki Alışlar" endpoint={`/products/${productId}/purchase-history`} testId="history-purchase" kind="movement" variants={variants} />
    </div>
  );
}

function AccordionSection({ title, endpoint, testId, kind, variants = [] }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const variantMap = useMemo(() => {
    const m = new Map();
    variants.forEach((v) => m.set(v.id, v));
    return m;
  }, [variants]);
  const variantLabel = (r) => {
    const v = variantMap.get(r.variant_id);
    if (v) return formatVariantLabel(v.attributes, r.variant_sku || "—");
    return r.variant_sku || "—";
  };

  const load = async () => {
    if (loaded) return;
    setLoading(true);
    try {
      const { data } = await api.get(endpoint);
      setRows(data || []); setLoaded(true);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (open) load(); }, [open]); // eslint-disable-line

  return (
    <Card className="rounded-sm bg-[#FCFCFD] dark:bg-card border border-slate-300 dark:border-border shadow-[0_1px_2px_rgba(16,24,40,0.05),0_8px_24px_-12px_rgba(16,24,40,0.10)] dark:shadow-md dark:shadow-black/30 overflow-hidden" data-testid={testId}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors"
        data-testid={`${testId}-toggle`}
      >
        <div className="flex items-center gap-2 text-sm font-semibold">
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          {title}
          {loaded && <span className="text-xs text-muted-foreground">({rows.length})</span>}
        </div>
      </button>
      {open && (
        <div className="border-t border-border">
          {loading ? (
            <div className="p-6 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : rows.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground text-center">Kayıt yok</div>
          ) : kind === "sales" ? (
            <table className="w-full text-sm">
              <thead className="bg-muted/40 border-b border-border">
                <tr className="text-[11px] uppercase tracking-wide font-medium text-slate-500 dark:text-muted-foreground">
                  <th className="text-left px-3 py-2">Tarih</th>
                  <th className="text-left px-3 py-2">Sipariş</th>
                  <th className="text-left px-3 py-2">Müşteri</th>
                  <th className="text-left px-3 py-2">Varyant</th>
                  <th className="text-right px-3 py-2">Adet</th>
                  <th className="text-right px-3 py-2">Birim</th>
                  <th className="text-right px-3 py-2">Toplam</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((r, i) => (
                  <tr key={i}>
                    <td className="px-3 py-1.5 text-xs">{fmtDate(r.date)}</td>
                    <td className="px-3 py-1.5 text-xs">{r.order_no || "—"}</td>
                    <td className="px-3 py-1.5 text-xs">{r.customer_name || "—"}</td>
                    <td className="px-3 py-1.5 text-xs font-semibold text-emerald-500 dark:text-emerald-400">{variantLabel(r)}</td>
                    <td className="px-3 py-1.5 text-right">{r.quantity}</td>
                    <td className="px-3 py-1.5 text-right">{fmt(r.unit_price)}</td>
                    <td className="px-3 py-1.5 text-right font-bold">{fmt(r.line_total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted/40 border-b border-border">
                <tr className="text-[11px] uppercase tracking-wide font-medium text-slate-500 dark:text-muted-foreground">
                  <th className="text-left px-3 py-2">Tarih</th>
                  <th className="text-left px-3 py-2">Tip</th>
                  <th className="text-left px-3 py-2">Depo</th>
                  <th className="text-left px-3 py-2">Varyant</th>
                  <th className="text-right px-3 py-2">Miktar</th>
                  <th className="text-left px-3 py-2">Sebep</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((r) => {
                  const meta = KIND_META[r.kind] || KIND_META.manuel;
                  return (
                    <tr key={r.id}>
                      <td className="px-3 py-1.5 text-xs">{fmtDate(r.created_at)}</td>
                      <td className={`px-3 py-1.5 text-xs font-semibold ${meta.color}`}>{meta.label}</td>
                      <td className="px-3 py-1.5 text-xs">{r.warehouse_name}</td>
                      <td className="px-3 py-1.5 text-xs font-semibold text-emerald-500 dark:text-emerald-400">{variantLabel(r)}</td>
                      <td className={`px-3 py-1.5 text-right ${r.quantity_delta > 0 ? "text-emerald-500" : "text-rose-500"}`}>
                        {r.quantity_delta > 0 ? "+" : ""}{fmt(r.quantity_delta)}
                      </td>
                      <td className="px-3 py-1.5 text-xs text-muted-foreground">{r.reason || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </Card>
  );
}

// ---- Images tab -----------------------------------------------------------
function ImagesTab({ productId, images, previews, onReload }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const f of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("label", f.name.replace(/\.[^.]+$/, ""));
        fd.append("is_primary", images.length === 0 ? "true" : "false");
        try {
          await api.post(`/products/${productId}/images/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        } catch (e) { toast.error(`${f.name}: ${formatApiError(e)}`); }
      }
      toast.success("Resimler yüklendi");
      onReload?.();
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (im) => {
    if (!window.confirm("Resim silinsin mi?")) return;
    try {
      await api.delete(`/products/${productId}/images/${im.id}`);
      onReload?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setPrimary = async (im) => {
    try {
      await api.patch(`/products/${productId}/images/${im.id}/primary`);
      toast.success("Ana resim güncellendi");
      onReload?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wide font-medium text-slate-500 dark:text-muted-foreground">
          {images.length} resim (max 50)
        </div>
        <div>
          <input
            ref={fileRef} type="file" multiple accept="image/*"
            className="hidden"
            data-testid="product-image-file-input"
            onChange={(e) => upload(e.target.files)}
          />
          <Button
            data-testid="product-image-upload-btn"
            onClick={() => fileRef.current?.click()}
            disabled={uploading || images.length >= 50}
            className="rounded-sm"
          >
            {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
            Resim Yükle
          </Button>
        </div>
      </div>

      {images.length === 0 ? (
        <Card className="rounded-sm border-dashed p-10 text-center">
          <ImageIcon className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <div className="font-bold mb-1">Henüz resim yok</div>
          <div className="text-sm text-muted-foreground mb-4">Ürün için sınırsız (max 50) resim ekleyebilirsiniz.</div>
        </Card>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {images.map((im) => (
            <Card
              key={im.id}
              data-testid={`product-image-${im.id}`}
              className={`rounded-sm bg-card overflow-hidden border ${im.is_primary ? "border-primary" : "border-border"}`}
            >
              <div className="aspect-square bg-muted flex items-center justify-center relative">
                {previews[im.id] ? (
                  <img src={previews[im.id]} alt="" className="max-w-full max-h-full object-contain" />
                ) : <ImageIcon className="w-8 h-8 text-muted-foreground" />}
                {im.is_primary && (
                  <div className="absolute top-1 left-1 bg-primary text-primary-foreground text-[9px] uppercase px-1.5 py-0.5 rounded-sm flex items-center gap-1">
                    <Star className="w-2.5 h-2.5" /> Ana
                  </div>
                )}
              </div>
              <div className="p-2">
                <div className="text-[11px] font-semibold truncate">{im.label || im.original_filename}</div>
                <div className="flex items-center gap-1 mt-1">
                  {!im.is_primary && (
                    <button
                      data-testid={`product-image-${im.id}-primary`}
                      onClick={() => setPrimary(im)}
                      className="text-[10px] text-primary hover:opacity-80 flex-1 flex items-center justify-center gap-0.5 py-0.5"
                    >
                      <Star className="w-2.5 h-2.5" /> Ana Yap
                    </button>
                  )}
                  <button
                    data-testid={`product-image-${im.id}-delete`}
                    onClick={() => remove(im)}
                    className="text-muted-foreground hover:text-rose-500 p-0.5"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ---- Linked products tab --------------------------------------------------
function LinkedTab({ product, onSave }) {
  const [allProducts, setAllProducts] = useState([]);
  const [links, setLinks] = useState(product.linked_products || []);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/products").then((r) => setAllProducts((r.data || []).filter((p) => p.id !== product.id))).catch(() => {});
  }, [product.id]);

  const addLink = () => setLinks((p) => [...p, { product_id: "", quantity_per_unit: 1 }]);
  const patchLink = (idx, key, val) => setLinks((p) => p.map((l, i) => (i === idx ? { ...l, [key]: val } : l)));
  const removeLink = (idx) => setLinks((p) => p.filter((_, i) => i !== idx));

  const save = async () => {
    setSaving(true);
    try {
      const cleaned = links.filter((l) => l.product_id && Number(l.quantity_per_unit) > 0).map((l) => ({
        product_id: l.product_id,
        quantity_per_unit: Number(l.quantity_per_unit),
      }));
      await api.patch(`/products/${product.id}`, { linked_products: cleaned });
      toast.success("Bağlı ürünler kaydedildi");
      onSave?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-blue-500/10 border border-blue-500/40 rounded-sm p-3 text-xs">
        <div className="flex items-start gap-2">
          <Link2 className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
          <div className="text-blue-600 dark:text-blue-400">
            <span className="font-semibold">Bağlı Ürünler:</span> Bu ürün satıldığında/üretildiğinde, aşağıda tanımlanan ürünler de tanımlı adet oranında stoktan otomatik düşülür. Örn: "İş Tişörtü" satılırken 1 adet "Askı" da düşülsün.
          </div>
        </div>
      </div>

      <div className="space-y-2">
        {links.map((l, idx) => (
          <div
            key={idx}
            data-testid={`linked-row-${idx}`}
            className="grid grid-cols-[1fr_120px_40px] gap-2 items-center bg-muted/30 border border-border rounded-sm p-2"
          >
            <Select value={l.product_id || "none"} onValueChange={(v) => patchLink(idx, "product_id", v === "none" ? "" : v)}>
              <SelectTrigger data-testid={`linked-${idx}-product`} className="rounded-sm">
                <SelectValue placeholder="Ürün seç" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="none">— seçilmedi —</SelectItem>
                {allProducts.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input
              data-testid={`linked-${idx}-qty`}
              type="number" step="0.01" min="0.01"
              value={l.quantity_per_unit}
              onChange={(e) => patchLink(idx, "quantity_per_unit", e.target.value)}
              className="rounded-sm text-right"
              placeholder="Adet/birim"
            />
            <button
              data-testid={`linked-${idx}-remove`}
              onClick={() => removeLink(idx)}
              className="text-muted-foreground hover:text-rose-500 p-1.5"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
        <Button data-testid="linked-add" onClick={addLink} variant="outline" size="sm" className="rounded-sm">
          <Plus className="w-3.5 h-3.5 mr-1" /> Bağlı Ürün Ekle
        </Button>
      </div>

      <div className="flex items-center justify-end pt-2 border-t border-border">
        <Button
          data-testid="linked-save"
          onClick={save} disabled={saving}
          className="rounded-sm bg-primary hover:bg-primary/90"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
          Kaydet
        </Button>
      </div>
    </div>
  );
}

// ---- Other info tab -------------------------------------------------------
function OtherTab({ product, onSave }) {
  const [category, setCategory] = useState(product.category || "");
  const [brand, setBrand] = useState(product.brand || "");
  const [shelf, setShelf] = useState(product.shelf_location || "");
  const [code, setCode] = useState(product.product_code || "");
  const [critical, setCritical] = useState(product.critical_stock || 0);
  const [saving, setSaving] = useState(false);
  const [categoryDefs, setCategoryDefs] = useState([]);
  const [brandDefs, setBrandDefs] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/definitions/product_category").catch(() => ({ data: [] })),
      api.get("/definitions/brand").catch(() => ({ data: [] })),
    ]).then(([c, b]) => {
      setCategoryDefs((c.data || []).filter((d) => d.is_active !== false));
      setBrandDefs((b.data || []).filter((d) => d.is_active !== false));
    });
  }, []);

  const categoryInList = useMemo(
    () => !category || categoryDefs.some((d) => d.name === category),
    [category, categoryDefs]
  );
  const brandInList = useMemo(
    () => !brand || brandDefs.some((d) => d.name === brand),
    [brand, brandDefs]
  );

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/products/${product.id}`, {
        category: category || null,
        brand: brand || null,
        shelf_location: shelf,
        product_code: code,
        critical_stock: parseInt(critical || 0, 10) || 0,
      });
      toast.success("Kaydedildi");
      onSave?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <Card className="rounded-sm bg-[#FCFCFD] dark:bg-card border border-slate-300 dark:border-border shadow-[0_1px_2px_rgba(16,24,40,0.05),0_8px_24px_-12px_rgba(16,24,40,0.10)] dark:shadow-md dark:shadow-black/30 p-5 space-y-4">
        <div className="text-[13px] font-semibold uppercase tracking-wider text-slate-800 dark:text-foreground">Diğer Bilgiler</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase text-muted-foreground">Kategori</Label>
            <Select
              value={category || "__none__"}
              onValueChange={(v) => setCategory(v === "__none__" ? "" : v)}
            >
              <SelectTrigger data-testid="other-category" className="rounded-sm mt-1 bg-slate-50 dark:bg-background border-slate-300 dark:border-input">
                <SelectValue placeholder="Kategori seçin" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Kategori Yok —</SelectItem>
                {!categoryInList && category && (
                  <SelectItem value={category}>{category} (özel)</SelectItem>
                )}
                {categoryDefs.map((d) => (
                  <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase text-muted-foreground">Marka</Label>
            <Select
              value={brand || "__none__"}
              onValueChange={(v) => setBrand(v === "__none__" ? "" : v)}
            >
              <SelectTrigger data-testid="other-brand" className="rounded-sm mt-1 bg-slate-50 dark:bg-background border-slate-300 dark:border-input">
                <SelectValue placeholder="Marka seçin" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Marka Yok —</SelectItem>
                {!brandInList && brand && (
                  <SelectItem value={brand}>{brand} (özel)</SelectItem>
                )}
                {brandDefs.map((d) => (
                  <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase text-muted-foreground">Ürün Kodu</Label>
            <Input data-testid="other-code" value={code} onChange={(e) => setCode(e.target.value)}
              className="rounded-sm mt-1 font-medium bg-slate-50 dark:bg-background border-slate-300 dark:border-input" placeholder="P-001" />
          </div>
          <div>
            <Label className="text-xs uppercase text-muted-foreground">Raf Yeri</Label>
            <Input data-testid="other-shelf" value={shelf} onChange={(e) => setShelf(e.target.value)}
              className="rounded-sm mt-1 font-medium bg-slate-50 dark:bg-background border-slate-300 dark:border-input" placeholder="Örn: A-12" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase text-muted-foreground flex items-center gap-1">
              <AlertTriangle className="w-3 h-3 text-amber-500" /> Kritik Stok Seviyesi
            </Label>
            <Input data-testid="other-critical" type="number" min="0" value={critical}
              onChange={(e) => setCritical(e.target.value)}
              className="rounded-sm mt-1 w-40" placeholder="0" />
            <div className="text-[11px] text-slate-500 dark:text-muted-foreground mt-1">
              Stok bu seviyenin altına düştüğünde uyarı gösterilir.
            </div>
          </div>
        </div>
      </Card>
      <div className="flex items-center justify-end">
        <Button data-testid="other-save" onClick={save} disabled={saving} className="rounded-sm">
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
          Kaydet
        </Button>
      </div>
    </div>
  );
}
